import React, { useState, useEffect } from 'react';
import { Grid, useMediaQuery, Typography, IconButton, Stack, Button, CircularProgress } from '@mui/material';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { RightContainer } from '../../components/RightContainer';
import WhiteInosio from '../../icons/whiteinosio.svg';
import randomquotes from '../../random_quotes.json';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ExtraLinks from './components/residentPage/ExtraLinks';
import { EmailRegex } from '../../utils/regex';
import { resetSelfInviteProfileStates, selectAuthState, selfInviteProfile } from '../sagas/auth/authSlice';
import { EntityTypeDescriptionEnum, SelfInviteProfileDto } from '@inosio/payments-sdk';

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    borderRadius: '24px',
  },
  rightContainer: {
    background: '#053742',
    width: '59%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    height: '100%',
  },
  leftContainer: {
    borderRadius: '24px 0px 0px 24px',
    background: '#ffffff',
    width: '41%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    position: 'relative',
  },
  residentBox: {
    width: '340px !important',
    margin: '10px 0 !important',
  },
  residentBoxMobile: {
    width: '88vw',
    margin: '10px 0 !important',
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  brandTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '24px',
  },
  buttonProgress: {
    color: '#053742',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  input: {
    border: 'none',
    outline: 'none',
    background: '#FFFFFF',
    boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
    borderRadius: '24px',
    height: '49px',
    maxWidth: '350px',
    width: '400px',
    padding: '0 10px',
    fontFamily: 'Work Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '22px',
  },
}));

export const OwnerPage = () => {
  const classes = useStyles();
  const matchesMobile = useMediaQuery('(max-width:380px)');
  const matches900 = useMediaQuery('(max-width:900px)');
  const matches1200 = useMediaQuery('(max-width:1200px)');
  const randomQuoteIndex = Math.floor(Math.random() * randomquotes.length);
  const loremIpsumPlaceholder: string = randomquotes[randomQuoteIndex].quote;
  const showCompanyName = false;
  const [emailAddress, setEmailAddress] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const { isSelfInvitingProfile, isSelfInvitingProfileFailed, isSelfInvitingProfileSuccess } = useSelector(selectAuthState);

  const navigation = useHistory();
  const dispatch = useDispatch();
  const goToPrevPage = () => {
    dispatch(resetSelfInviteProfileStates());
    navigation.push({
      pathname: '/signup',
    });
  };

  useEffect(() => {
    if (EmailRegex.test(emailAddress)) {
      setIsValidEmail(true);
    } else {
      setIsValidEmail(false);
    }
  }, [emailAddress]);

  const handleSearch = () => {
    if (isValidEmail) {
      dispatch({
        type: selfInviteProfile.type,
        payload: {
          email: emailAddress,
          entityTypeDescription: EntityTypeDescriptionEnum.Owner,
        } as SelfInviteProfileDto,
      });
    }
  };

  return (
    <div className={classes.rootContainer}>
      {/* Left part of page */}
      <Grid sx={{ flexDirection: 'column', width: !matches900 ? '41%' : '100%' }} className={classes.leftContainer}>
        {isSelfInvitingProfileFailed && (
          <Box sx={{ margin: '80px 0', padding: '0 20px' }}>
            <Typography
              style={{
                maxWidth: '435px',
                margin: '0 10px',
                borderTop: '2px  solid #DB3939',
                borderRadius: ' 24px',
                padding: '7px 20px',
                fontSize: '12px',
                color: '#DB3939',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              {'Apologies, we were not able to find you by this email. Please try again with a different email or contact your Property Manager.'}
            </Typography>
          </Box>
        )}
        {isSelfInvitingProfileSuccess && (
          <Box sx={{ margin: '60px 0 100px', padding: '0 20px' }}>
            <Typography
              style={{
                maxWidth: '420px',
                margin: '0 10px',
                background: ' #F1F0F5',
                borderRadius: ' 24px',
                fontSize: '12px',
                padding: '15px',
              }}
            >
              {`Thank you! If the email address you provided is in our system, we’ve sent an invitation link to it. Please check your inbox and click the link to complete your registration. If you don’t see the email, check your spam folder or try again.`}
            </Typography>
          </Box>
        )}
        {/* Inosio brand */}
        <Box className={classes.brandContainer} pb={3} sx={{ width: matchesMobile ? '88vw' : '340px' }}>
          {/* Go to prev page */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="go to prev"
            onClick={goToPrevPage}
            sx={{
              position: 'absolute',
              left: 0,
              top: '-8px',
            }}
          >
            <ArrowCircleLeftIcon sx={{ width: '30px', height: '30px', fill: '#39A2DB' }} />
          </IconButton>

          <img src={WhiteInosio} style={{ width: '78px', height: '78px', marginLeft: showCompanyName ? '40px' : '0' }} alt="logo" />
        </Box>
        <Stack direction="column" justifyContent="center" alignItems="center" spacing={4} sx={{ marginTop: '30px' }}>
          <Box sx={{ width: '100%' }}>
            <input placeholder="Enter your email address" value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} className={classes.input} />
          </Box>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ width: '100%', flexDirection: matchesMobile ? 'column' : 'row' }}>
            <Button
              type="submit"
              variant="contained"
              style={{
                width: matchesMobile ? '100%' : '113px',
                marginTop: matchesMobile ? '20px' : '',
                height: '49px',
                fontSize: '18px',
                borderRadius: '24px',
                textTransform: 'capitalize',
                color: 'white',
                backgroundColor: isValidEmail ? '#39A2DB' : 'rgba(211, 211, 211, 1)',
                borderColor: isValidEmail ? '#39A2DB' : 'rgba(211, 211, 211, 1)',
              }}
              onClick={handleSearch}
              disabled={!isValidEmail}
            >
              Search
              {isSelfInvitingProfile ? <CircularProgress size={26} className={classes.buttonProgress} /> : <></>}
            </Button>
          </Stack>

          <ExtraLinks />
        </Stack>
      </Grid>

      {/* Right part of page */}
      {!(matches900 && matches1200) ? (
        <Grid className={classes.rightContainer}>
          <RightContainer textPlaceholder={loremIpsumPlaceholder} />
        </Grid>
      ) : (
        <></>
      )}
    </div>
  );
};
